import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';
import { BrandMark } from '@nucleus-commerce/admin-kit/layout';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';

import { LegalLinks } from '@/shared/legal/legal-links';

import { SignInCard } from '../components/sign-in-card';

export interface LandingPageProps {
  /** Where to go after sign-in; the organisation home by default. */
  redirectTo?: string | undefined;
  /** `?organization=`, handed to the sign-in card. */
  organizationSlug?: string | undefined;
  /** Why the last session ended, when it was not an ordinary expiry. */
  notice?: 'support_session_ended' | undefined;
}

const PILLARS = ['variants', 'madeToOrder', 'oneLogin'] as const;

/** Portal landing for visitors without a session: brand, value statement, sign-in card. */
export function LandingPage({ redirectTo, organizationSlug, notice }: LandingPageProps) {
  const { t } = useTranslation('auth');
  const config = getRuntimeConfig();
  const docsUrl = config.docsUrl ?? `${config.apiBaseUrl}/docs`;
  const statusUrl = `${config.apiBaseUrl}/health`;

  return (
    <div className="bg-muted/40 flex min-h-svh flex-col lg:flex-row">
      <section className="bg-sidebar flex flex-col justify-between gap-10 border-b px-6 py-8 lg:w-[57%] lg:border-r lg:border-b-0 lg:px-16 lg:py-12">
        <div className="flex items-center gap-3">
          <BrandMark size={28} />
          <span className="text-lg font-semibold tracking-tight">{t('landing.brand')}</span>
          <span className="text-muted-foreground border-l pl-3 text-[13px]">
            {t('landing.surface')}
          </span>
        </div>
        <div className="flex max-w-[600px] flex-col gap-6 lg:gap-7">
          <h1 className="font-serif text-4xl leading-[1.05] font-normal tracking-tight sm:text-5xl lg:text-[56px]">
            {t('landing.headline')}
          </h1>
          <p className="text-muted-foreground max-w-[520px] text-base leading-relaxed sm:text-lg">
            {t('landing.lede')}
          </p>
          <div className="grid gap-5 pt-1 sm:grid-cols-3 sm:gap-8">
            {PILLARS.map((key) => (
              <div key={key} className="flex flex-col gap-1">
                <span className="text-[13px] font-semibold">
                  {t(`landing.pillars.${key}.title`)}
                </span>
                <span className="text-muted-foreground text-[13px] leading-snug">
                  {t(`landing.pillars.${key}.body`)}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col-reverse gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div
            className="bg-primary-tint text-primary-tint-foreground border-primary/50 flex h-[150px] w-full max-w-[300px] items-center justify-center rounded-xl border border-dashed text-[13px] lg:h-[190px]"
            aria-hidden
          >
            {t('landing.imagePlaceholder')}
          </div>
          <nav
            aria-label={t('landing.footerNav')}
            className="text-muted-foreground flex gap-5 text-[13px]"
          >
            <a href={statusUrl} target="_blank" rel="noreferrer" className="hover:text-foreground">
              {t('landing.links.status')}
            </a>
            <a href={docsUrl} target="_blank" rel="noreferrer" className="hover:text-foreground">
              {t('landing.links.docs')}
            </a>
          </nav>
        </div>
      </section>
      <section className="flex flex-1 flex-col items-center justify-center gap-5 px-4 py-10 lg:p-12">
        {notice ? (
          <Alert role="status" className="max-w-[400px]" data-testid="sign-in-notice">
            <AlertDescription>{t(`login.notice.${notice}`)}</AlertDescription>
          </Alert>
        ) : null}
        <SignInCard redirectTo={redirectTo} organizationSlug={organizationSlug} />
        <p className="text-muted-foreground text-sm">
          {t('landing.newBrand')}{' '}
          <Link
            to="/apply"
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            {t('landing.apply')}
          </Link>
        </p>
        <LegalLinks className="text-muted-foreground [&_a:hover]:text-foreground text-xs [&_a]:underline-offset-4 [&_a:hover]:underline" />
      </section>
    </div>
  );
}
